import { createSlice } from '@reduxjs/toolkit';
import { RenewState } from 'src/types/flow';

const INITIAL_STATE: RenewState = {
  initiatingRenewFlow: false,
  initiatingRenewFlowError: '',

  renewLicenseInProgress: false,
  renewLicenseError: '',

  documentUploadStarted: false,
  documentUploadWorking: false,
  documentUploadCompleted: false,
  documentUploadErrors: [],

  applicationData: {},
  applicationNumber: '',
  licenseNumber: '',
  lateRenewalData: {}, // if populated should have properties of hasExpired, expirationDate and renewBy (used on confirmation page f there are late fees)
  selectedBulkRenewalLicenses: [],
};

export const renewSlice = createSlice({
  name: 'renew',
  initialState: INITIAL_STATE,
  // Reducers describe operations on state. Under the hood, reducers take the
  // current state and modify it in some manner then return the modified state.
  // Redux stores the modified state as the current state.  Subsequent reducers
  // will then operate on the modified state - thus, state changes accumulate/persist
  reducers: {
    setInitiatingRenewFlowInProgress: (state) => {
      return {
        ...state,
        initiatingRenewFlow: true,
        initiatingRenewFlowError: '',
        documentUploadStarted: false,
        documentUploadWorking: false,
        documentUploadCompleted: false,
      };
    },
    setInitiatingRenewFlowComplete: (state, action) => {
      state.initiatingRenewFlow = false;
      state.initiatingRenewFlowError = action.payload;
    },
    setRenewLicenseInProgress: (state) => {
      state.renewLicenseInProgress = true;
      state.documentUploadErrors = [];
      state.renewLicenseError = '';
    },
    setRenewLicenseCompleted: (state) => {
      state.renewLicenseInProgress = false;
    },
    setRenewLicenseError: (state, action) => {
      state.renewLicenseError = action.payload;
    },
    setBulkRenewalLicenses: (state, action) => {
      state.selectedBulkRenewalLicenses = action.payload;
    },
    clearBulkRenewalLicenses: (state) => {
      state.selectedBulkRenewalLicenses = [];
    },
    updatePermitData: (state, action) => {
      state.applicationData = {
        ...state.applicationData,
        ...action.payload,
      };
    },
    removeRegistrantData: (state) => {
      if (state.applicationData) delete state.applicationData.registrant;
    },
    removeContactsData: (state) => {
      if (state.applicationData) delete state.applicationData.contacts;
    },
    removePropertyDetailsData: (state) => {
      if (state.applicationData) delete state.applicationData.propertyDetails;
    },
    removeListingsData: (state) => {
      if (state.applicationData) delete state.applicationData.listings;
    },
    setRenewApplicationNumber: (state, action) => {
      state.applicationNumber = action.payload;
    },
    setRenewLicenseNumber: (state, action) => {
      state.licenseNumber = action.payload ?? '';
    },
    setLateRenewalData: (state, action) => {
      state.lateRenewalData = action.payload;
    },
    setDocumentUploadWorking: (state, action) => ({
      ...state,
      documentUploadStarted: false,
      documentUploadCompleted: false,
      documentUploadWorking: action.payload,
    }),
    setDocumentUploadCompleted: (state, action) => ({
      ...state,
      documetUploadStartd: false,
      documentUploadWorking: false,
      documentUploadCompleted: action.payload,
    }),
    setDocumentUploadError: (state, action) => ({
      ...state,
      documentUploadErrors: [...state.documentUploadErrors, action.payload],
    }),
    resetDocumentUploadProgressStatus: (state) => ({
      ...state,
      documentUploadErrors: [],
      documentUploadStarted: false,
      documentUploadCompleted: false,
      documentUploadWorking: false,
    }),
    resetRenewState: () => INITIAL_STATE,
  },
});

export const {
  setInitiatingRenewFlowInProgress,
  setInitiatingRenewFlowComplete,
  updatePermitData,
  removeRegistrantData,
  removeContactsData,
  removeListingsData,
  removePropertyDetailsData,
  setRenewApplicationNumber,
  setRenewLicenseNumber,
  setDocumentUploadWorking,
  setDocumentUploadCompleted,
  setDocumentUploadError,
  resetDocumentUploadProgressStatus,
  resetRenewState,
  setRenewLicenseInProgress,
  setRenewLicenseCompleted,
  setRenewLicenseError,
  setLateRenewalData,
  setBulkRenewalLicenses,
  clearBulkRenewalLicenses,
} = renewSlice.actions;

export default renewSlice.reducer;
